import React from "react";
import { Link } from "react-router-dom";
import { LifeBuoy } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import useMediaQuery, { DESKTOP_QUERY } from "@/hooks/useMediaQuery";

/**
 * One FAQ, two layouts, one source of copy.
 *
 * WHY: HeyCatch recorded repeated rage-clicks on phones against the questions
 * that matter most before someone trusts us ("Are sponsors vetted?", "Is my
 * information private?", "What happens if a sponsor is unavailable?", "What
 * does it cost?"). On a phone an accordion puts a tap between a worried person
 * and the answer, and the taps were not landing reliably. So:
 *
 *   * Phones  -> every answer is already in the document. No tap required.
 *   * Desktop -> the accordion stays (it works well with a mouse).
 *   * Both read the SAME `items` array, so the two can never drift apart.
 *   * Only one layout is MOUNTED at a time (useMediaQuery rather than
 *     `hidden md:block`), so screen readers never meet two copies of every
 *     answer and nothing inside a panel can fire twice.
 *
 * The copy is never rewritten here. Answers accept a string or an array of
 * paragraphs exactly as the calling page already stores them.
 */

const linkClasses =
  "inline-flex items-center gap-1.5 min-h-[44px] rounded-xl border border-[#E3DED4] bg-white px-4 py-2.5 text-sm font-semibold text-[#1F6F78] transition-colors duration-150 hover:bg-[#E9F3F3] active:bg-[#D7EEF0] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1F6F78]";

const paragraphsOf = (answer) => (Array.isArray(answer) ? answer : [answer]);

function AnswerBody({ item, testIdPrefix }) {
  return (
    <>
      {paragraphsOf(item.a).map((p, i) => (
        <p key={`${item.id}-a${i}`} className="text-[#4B5A60] leading-7 break-words">
          {p}
        </p>
      ))}
      {item.links?.length ? (
        <div className="flex flex-wrap gap-2 pt-1">
          {item.links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`${testIdPrefix}-link-${item.id}`}
              className={linkClasses}
            >
              {l.to === "/resources" && <LifeBuoy className="w-4 h-4" aria-hidden="true" />}
              {l.label}
            </Link>
          ))}
        </div>
      ) : null}
    </>
  );
}

export default function ResponsiveFaq({ items, testIdPrefix, priorityIds = [], className = "" }) {
  const isDesktop = useMediaQuery(DESKTOP_QUERY);

  // On phones the questions people were trying to open come first. Nothing is
  // added or removed - only the order of the same list changes.
  const mobileItems = React.useMemo(() => {
    if (!priorityIds.length) return items;
    const first = priorityIds.map((id) => items.find((i) => i.id === id)).filter(Boolean);
    const rest = items.filter((i) => !priorityIds.includes(i.id));
    return [...first, ...rest];
  }, [items, priorityIds]);

  if (isDesktop) {
    return (
      <Accordion
        type="single"
        collapsible
        data-testid={`${testIdPrefix}-accordion`}
        className={`space-y-3 ${className}`}
      >
        {items.map((item) => (
          <AccordionItem
            key={item.id}
            value={item.id}
            data-testid={`${testIdPrefix}-${item.id}`}
            className="rounded-2xl border border-[#E3DED4] bg-[#FBF9F5] px-5 data-[state=open]:border-[#1F6F78] data-[state=open]:bg-white"
          >
            {/* AccordionTrigger renders a real <button> that fills the row and
                owns aria-expanded / aria-controls, so the entire row is the hit
                area and keyboard behaviour is native. */}
            <AccordionTrigger
              data-testid={`${testIdPrefix}-trigger-${item.id}`}
              className="w-full text-left min-h-[56px] font-display text-base sm:text-lg font-bold text-[#1F2A2E] hover:no-underline"
            >
              {item.q}
            </AccordionTrigger>
            <AccordionContent
              data-testid={`${testIdPrefix}-answer-${item.id}`}
              className="pb-5 space-y-3"
            >
              <AnswerBody item={item} testIdPrefix={testIdPrefix} />
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    );
  }

  return (
    <div data-testid={`${testIdPrefix}-mobile-list`} className={`space-y-4 ${className}`}>
      {mobileItems.map((item) => (
        <article
          key={item.id}
          data-testid={`${testIdPrefix}-${item.id}`}
          className="rounded-2xl border border-[#E3DED4] bg-[#FBF9F5] px-5 py-5"
        >
          <h3
            data-testid={`${testIdPrefix}-question-${item.id}`}
            className="font-display text-base font-bold text-[#1F2A2E] leading-7 break-words"
          >
            {item.q}
          </h3>
          <div data-testid={`${testIdPrefix}-answer-${item.id}`} className="mt-3 space-y-3">
            <AnswerBody item={item} testIdPrefix={testIdPrefix} />
          </div>
        </article>
      ))}
    </div>
  );
}
